// TODO: frames
import { type AccentHue, colorize } from "@repo/fika-design-system/color"
import { createDropzone, createFileUploader } from "@solid-primitives/upload"
import { createSignal, type ParentProps, Show } from "solid-js"
import explosionAnimation from "@/pages/posts/explosion.gif"
import { Button } from "../components/button"
import { Icon } from "../components/icon"
import { Text } from "../components/text"
import { deleteFile, dropzoneRecipe, explosionClass } from "./file-upload.css"

type Props = {
  onUpload: (files: File[]) => void
  text?: string
  hue: AccentHue
  accept?: string
  iconName?: string
  disabled?: boolean
  hasFile?: boolean
  multiple?: boolean
  variant?: "default" | "profile"
} & ParentProps

export function FileUpload(props: Props) {
  const { selectFiles } = createFileUploader({
    accept: props.accept,
    multiple: props.multiple,
  })
  const [dragOver, setDragOver] = createSignal(false)
  const [bounce, setBounce] = createSignal(false)
  const [explode, setExplode] = createSignal(false)

  async function onUpload() {
    if (props.disabled) return

    selectFiles(async files => {
      setBounce(true)

      const uploadedFiles = files.map(file => file.file)
      props.onUpload(uploadedFiles)
      setTimeout(() => setBounce(false), 1_500)
    })
  }

  async function onDelete(ev?: MouseEvent) {
    if (ev) ev.stopPropagation()

    setExplode(true)
    setBounce(true)
    setTimeout(() => {
      props.onUpload([])

      setTimeout(() => {
        setExplode(false)
        setBounce(false)
      }, 1_000)
    }, 500)
  }

  const { setRef: dropzoneRef } = createDropzone({
    onDrop: async files => {
      if (files.length === 0 || props.disabled) return

      setBounce(true)
      setDragOver(false)
      const uploadedFiles = files.map(file => file.file)
      props.onUpload(uploadedFiles)
      setTimeout(() => setBounce(false), 1_500)
    },
    onDragEnter: () => {
      if (!props.disabled) setDragOver(true)
    },
    onDragLeave: () => {
      setDragOver(false)
    },
  })

  async function onKeyDown(ev: KeyboardEvent) {
    if (ev.key === "Enter" || ev.key === " ") {
      ev.preventDefault()
      await onUpload()
    }
  }

  const hasFile = () => props.hasFile ?? false

  return (
    <div
      ref={dropzoneRef}
      class={dropzoneRecipe({
        dragOver: dragOver(),
        bounce: bounce(),
        withFile: hasFile(),
        hue: props.hue,
        disabled: props.disabled,
        variant: props.variant,
      })}
      onClick={onUpload}
      onKeyDown={onKeyDown}
    >
      <Show when={explode()}>
        <img
          alt="explosion"
          class={explosionClass}
          src={explosionAnimation}
          width="300"
        />
      </Show>
      <Show
        when={hasFile() && props.children}
        fallback={
          <>
            <Icon
              color={colorize(props.hue, "600")}
              name={(props.iconName as any) || "upload-cloud"}
            />
            <Show when={props.text}>
              <Text fontSize="l" color={colorize(props.hue, "700")}>
                {props.text}
              </Text>
            </Show>
          </>
        }
      >
        <Button
          iconName="trash-2"
          hue="amber"
          type="button"
          class={deleteFile}
          onClick={onDelete}
        />
        {props.children}
      </Show>
    </div>
  )
}
